import {
  SIZE_COLOR_STOCK_SEP,
  comboStockKey,
  getVariantMode,
  normalizeLabelCompare,
  normalizeOptionList,
  parseComboStockKey,
} from "./variantStock";

/** @param {unknown} raw */
export function coerceVariantImageMap(raw) {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const sk = String(k ?? "").trim();
    if (!sk) continue;
    if (typeof v === "number" && Number.isFinite(v)) {
      const idx = Math.floor(v);
      if (idx >= 0) out[sk] = idx;
      continue;
    }
    const url = String(v ?? "").trim();
    if (url) out[sk] = url;
  }
  return Object.keys(out).length ? out : null;
}

/** Find map key for a single label (size or color). */
export function resolveVariantImageLabelKey(map, label) {
  if (!map) return null;
  const lab = String(label ?? "").trim();
  if (!lab) return null;
  if (Object.prototype.hasOwnProperty.call(map, lab)) return lab;
  const norm = normalizeLabelCompare(lab);
  for (const k of Object.keys(map)) {
    if (k.includes(SIZE_COLOR_STOCK_SEP)) continue;
    if (normalizeLabelCompare(k) === norm) return k;
  }
  return null;
}

/** Find map key for size+color combo. */
export function resolveVariantImageComboKey(map, size, color) {
  if (!map) return null;
  const sz = String(size ?? "").trim();
  const cz = String(color ?? "").trim();
  if (!sz || !cz) return null;
  const exact = comboStockKey(sz, cz);
  if (Object.prototype.hasOwnProperty.call(map, exact)) return exact;
  const nsz = normalizeLabelCompare(sz);
  const ncz = normalizeLabelCompare(cz);
  for (const k of Object.keys(map)) {
    if (!k.includes(SIZE_COLOR_STOCK_SEP)) continue;
    const { size: ks, color: kc } = parseComboStockKey(k);
    if (normalizeLabelCompare(ks) === nsz && normalizeLabelCompare(kc) === ncz) {
      return k;
    }
  }
  return null;
}

/**
 * Resolve linked image URL for the current size/color selection.
 * @returns {string|null}
 */
export function resolveVariantImageUrl(product, selectedSize, selectedColor) {
  const map = coerceVariantImageMap(product?.variant_images);
  if (!map) return null;

  const sizes = normalizeOptionList(product?.sizes);
  const colors = normalizeOptionList(product?.colors);
  const sz = String(selectedSize ?? "").trim();
  const cz = String(selectedColor ?? "").trim();

  if (sizes.length > 0 && colors.length > 0) {
    if (sz && cz) {
      const comboKey = resolveVariantImageComboKey(map, sz, cz);
      if (comboKey) {
        const v = map[comboKey];
        return typeof v === "string" ? v : null;
      }
    }
    return null;
  }

  if (sizes.length > 0 && sz) {
    const key = resolveVariantImageLabelKey(map, sz);
    if (key) {
      const v = map[key];
      return typeof v === "string" ? v : null;
    }
  }

  if (colors.length > 0 && cz) {
    const key = resolveVariantImageLabelKey(map, cz);
    if (key) {
      const v = map[key];
      return typeof v === "string" ? v : null;
    }
  }

  return null;
}

/** @param {unknown} img */
export function imageUrlOf(img) {
  if (img == null) return "";
  if (typeof img === "string") return img.trim();
  return String(img?.image_url || img?.url || "").trim();
}

/**
 * Find gallery index for the linked variant image.
 * @returns {number|null}
 */
export function resolveVariantImageIndex(product, selectedSize, selectedColor) {
  const url = resolveVariantImageUrl(product, selectedSize, selectedColor);
  if (!url) return null;
  const images = Array.isArray(product?.images) ? product.images : [];
  const idx = images.findIndex((img) => imageUrlOf(img) === url);
  return idx >= 0 ? idx : null;
}

/**
 * Rows for the optional admin linker UI.
 * @returns {Array<{ key: string, label: string }>}
 */
export function buildVariantImageLinkRows({ enableSizes, enableColors, sizes = [], colors = [] }) {
  const mode = getVariantMode(enableSizes === true, enableColors === true);
  const sizeList = normalizeOptionList(sizes);
  const colorList = normalizeOptionList(colors);
  if (mode === "sizes") {
    return sizeList.map((s) => ({ key: s, label: s }));
  }
  if (mode === "colors") {
    return colorList.map((c) => ({ key: c, label: c }));
  }
  if (mode === "both") {
    const rows = [];
    for (const s of sizeList) {
      for (const c of colorList) {
        rows.push({
          key: comboStockKey(s, c),
          label: `${c} · ${s}`,
        });
      }
    }
    return rows;
  }
  return [];
}

/**
 * Remap numeric image indices after removing an image at `removedIndex`.
 * @param {Record<string, unknown>} map
 * @param {number} removedIndex
 */
export function remapVariantImageIndicesAfterRemove(map, removedIndex) {
  const prev = map && typeof map === "object" ? map : {};
  const next = {};
  const ri = Math.floor(Number(removedIndex));
  for (const [k, v] of Object.entries(prev)) {
    if (typeof v === "number" && Number.isFinite(v)) {
      const idx = Math.floor(v);
      if (idx === ri) continue;
      next[k] = idx > ri ? idx - 1 : idx;
      continue;
    }
    const s = String(v ?? "");
    if (s.startsWith("new:")) {
      const idx = Number(s.slice(4));
      if (!Number.isFinite(idx) || idx === ri) continue;
      next[k] = `new:${idx > ri ? idx - 1 : idx}`;
      continue;
    }
    if (s) next[k] = s;
  }
  return next;
}

/**
 * Drop links that point at a removed existing image URL.
 * @param {Record<string, unknown>} map
 * @param {string} removedUrl
 */
export function dropVariantImageLinksForUrl(map, removedUrl) {
  const url = String(removedUrl ?? "").trim();
  if (!url) return map && typeof map === "object" ? { ...map } : {};
  const prev = map && typeof map === "object" ? map : {};
  const next = {};
  for (const [k, v] of Object.entries(prev)) {
    if (String(v ?? "").trim() === url) continue;
    next[k] = v;
  }
  return next;
}

/**
 * Build a URL/index map for FormData from admin linker state.
 * Only keys listed in `allowedKeys` (if provided) are kept.
 * @param {Record<string, unknown>} byKey - key -> url | index | "new:N"
 * @param {{ existingUrls?: string[], newUploadedUrls?: string[], createUploadedUrls?: string[]|null, allowedKeys?: string[]|null }} [opts]
 */
export function buildVariantImagesPayload(byKey, {
  existingUrls = [],
  newUploadedUrls = [],
  createUploadedUrls = null,
  allowedKeys = null,
} = {}) {
  const src = byKey && typeof byKey === "object" ? byKey : {};
  const allow =
    Array.isArray(allowedKeys) && allowedKeys.length > 0
      ? new Set(allowedKeys.map((k) => String(k).trim()).filter(Boolean))
      : null;
  const out = {};
  for (const [k, v] of Object.entries(src)) {
    const key = String(k ?? "").trim();
    if (!key) continue;
    if (allow && !allow.has(key)) continue;

    if (createUploadedUrls && typeof v === "number" && Number.isFinite(v)) {
      const idx = Math.floor(v);
      const url = createUploadedUrls[idx];
      if (url) out[key] = url;
      continue;
    }

    if (typeof v === "number" && Number.isFinite(v)) {
      // create: send index for backend to resolve
      out[key] = Math.floor(v);
      continue;
    }

    const s = String(v ?? "").trim();
    if (!s) continue;
    if (s.startsWith("new:")) {
      const idx = Number(s.slice(4));
      if (!Number.isFinite(idx)) continue;
      const url = newUploadedUrls[Math.floor(idx)];
      if (url) out[key] = url;
      continue;
    }
    if (existingUrls.includes(s) || !existingUrls.length) {
      out[key] = s;
    }
  }
  return Object.keys(out).length ? out : null;
}

/** Append variant_images JSON to FormData (optional). */
export function appendVariantImagesToFormData(formData, mapOrNull, { isUpdate = false } = {}) {
  if (!formData) return;
  if (mapOrNull && typeof mapOrNull === "object" && Object.keys(mapOrNull).length > 0) {
    formData.append("variant_images", JSON.stringify(mapOrNull));
  } else if (isUpdate) {
    formData.append("variant_images", "");
  }
}
